import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import keycode from 'keycode';
import styled, { css, keyframes } from 'styled-components';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var toggleCache = {};

function guard(name, onFunction, offFunction) {
  if (toggleCache[name]) {
    return toggleCache[name];
  }

  var globalCounter = 0;
  var activeTokens = {};
  var thing = {};
  var thingCounter = 0;
  var toggle = {
    register: function register() {
      globalCounter += 1;
      var token = globalCounter;
      activeTokens[token] = true;
      return token;
    },
    deregister: function deregister(token) {
      if (!activeTokens[token]) {
        return false;
      }

      activeTokens[token] = false;
      toggle.off(token);
      return true;
    },
    on: function on(token) {
      if (thing[token]) {
        return false;
      }

      thing[token] = true;

      if (!thingCounter) {
        onFunction();
      }

      thingCounter += 1;
      return true;
    },
    off: function off(token) {
      if (!thing[token]) {
        return false;
      }

      thing[token] = false;
      thingCounter -= 1;

      if (!thingCounter) {
        offFunction();
      }

      return true;
    }
  };
  toggleCache[name] = toggle;
  return toggle;
}

var bodyGuard = guard('bodyOverflow', function () {
  document.body.style.overflow = 'auto';
}, function () {
  document.body.style.overflow = '';
});

var Icon = styled.svg.withConfig({
  displayName: "style__Icon",
  componentId: "sc-sizmc5-0"
})(["display:inline-block;height:22px;width:22px;", " ", " ", " ", " ", " ", ""], function (props) {
  return props.size === 'xxxs' && css(["height:10px;width:10px;"]);
}, function (props) {
  return props.size === 'xxs' && css(["height:12px;width:12px;"]);
}, function (props) {
  return (props.size === 'xs' || props.size === 'x-small') && css(["height:16px;width:16px;"]);
}, function (props) {
  return (props.size === 'm' || props.size === 'medium') && css(["height:32px;width:32px;"]);
}, function (props) {
  return (props.size === 'l' || props.size === 'large') && css(["height:48px;width:48px;"]);
}, function (props) {
  return (props.size === 'xl' || props.size === 'x-large') && css(["height:64px;width:64px;"]);
});

var Icon$1 = function Icon$1(_ref) {
  var path = _ref.path,
      pathMobile = _ref.pathMobile,
      size = _ref.size,
      sizeMobile = _ref.sizeMobile,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["path", "pathMobile", "size", "sizeMobile", "className"]);

  var isClient = typeof window !== 'undefined';

  var _useState = useState({
    smallScreen: isClient && window && window.innerWidth < 768
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var handleResize = useCallback(function () {
    setState({
      smallScreen: isClient && window && window.innerWidth < 768
    });
  });
  useEffect(function () {
    if (!isClient) {
      return false;
    }

    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  var iconSize = state.smallScreen && sizeMobile ? sizeMobile : size;
  var iconPath = state.smallScreen && pathMobile ? pathMobile : path;

  if (!path) {
    return false;
  }

  return React.createElement(Icon, _extends({
    viewBox: "0 0 32 32",
    xmlns: "https://www.w3.org/2000/svg",
    className: className,
    size: iconSize
  }, rest), React.createElement("path", {
    className: "sg-icon__path",
    d: iconPath
  }));
};

var sizes = ['xxxs', 'xxs', 'xs', 'x-small', 'm', 's', 'small', 'medium', 'l', 'large', 'xl', 'x-large'];
Icon$1.propTypes = {
  size: PropTypes.oneOf(sizes),
  sizeMobile: PropTypes.oneOf(sizes),
  path: PropTypes.string,
  pathMobile: PropTypes.string,
  className: PropTypes.string
};
Icon$1.defaultProps = {
  size: null,
  sizeMobile: null,
  path: null,
  pathMobile: null,
  className: null
};

/* eslint-disable max-len */
var USER_INTERFACE_CROSS_CANCEL = 'M17.589,16,31.671,30.081a1.124,1.124,0,0,1-1.59,1.589L16,17.589,1.919,31.67a1.124,1.124,0,1,1-1.59-1.589L14.411,16,.329,1.919A1.124,1.124,0,0,1,1.919.33L16,14.411,30.081.33a1.124,1.124,0,0,1,1.59,1.589Z';

var colors = {
  // Core
  darkCore: '#ff5e00',
  core: '#fe8c00',
  sunlight: '#fff496',
  // Sale
  rouge: '#ff5a52',
  // Label
  warmBlack: '#323220',
  grass: '#158620',
  // Primary colors
  wave: '#3d6778',
  lightTeal: '#b0ccca',
  silver: '#d6e2e5',
  olive: '#70763d',
  lightOlive: '#c0cba2',
  hazel: '#8f5000',
  sand: '#eee1d2',
  // UI colors
  black: '#000',
  almostblack: '#202020',
  darkGray: '#454545',
  darkGrayL1: '#707070',
  mediumGray: '#adadad',
  mediumGrayL1: '#c7c7c7',
  lightGray: '#e3e3e3',
  lightGrayL1: '#f7f7f7',
  white: '#fff',
  lightSand: '#f9f5f1',
  lightSandBox: '#fdf8cf',
  ocean: '#359cbb',
  oceanL1: '#60b9d4',
  morello: '#e42a25',
  morelloL1: '#e74b3f',
  spreadShadow: '#dfdfdf',
  // Globetrotter
  darkGlobe: '#172b54',
  darkGlobeL1: '#3a4b6d',
  globe: '#7594b3',
  globeL1: '#89a4be',
  latte: '#a48c6a',
  lightRouge: '#fabaac',
  mora: '#4c1029',
  moraL1: '#673448',
  // Spies
  mango: '#f6b637',
  // Other
  tripadvisor: '#589442',
  // Validation error
  validationErrorBg: '#facac7',
  validationErrorText: '#8f271f',
  validationErrorBorder: '#fd9e99',
  // Validation
  validationValid: '#1d9886'
};
var transparentColors = {
  transparent: 'rgba(255, 255, 255, 0)',
  semiTransparent: 'rgba(255, 255, 255, .5)',
  semiTransparent10: 'rgba(255, 255, 255, .1)',
  semiTransparent20: 'rgba(255, 255, 255, .2)',
  semiTransparent30: 'rgba(255, 255, 255, .3)',
  semiTransparent40: 'rgba(255, 255, 255, .4)',
  semiTransparent50: 'rgba(255, 255, 255, .5)',
  semiTransparent60: 'rgba(255, 255, 255, .6)',
  semiTransparent70: 'rgba(255, 255, 255, .7)',
  semiTransparent80: 'rgba(255, 255, 255, .8)',
  semiTransparent90: 'rgba(255, 255, 255, .9)',
  semiTransparentblack: 'rgba(0, 0, 0, .5)',
  semiTransparentblack10: 'rgba(0, 0, 0, .1)',
  semiTransparentblack20: 'rgba(0, 0, 0, .2)',
  semiTransparentblack30: 'rgba(0, 0, 0, .3)',
  semiTransparentblack40: 'rgba(0, 0, 0, .4)',
  semiTransparentblack50: 'rgba(0, 0, 0, .5)',
  semiTransparentblack60: 'rgba(0, 0, 0, .6)',
  semiTransparentblack70: 'rgba(0, 0, 0, .7)',
  semiTransparentblack80: 'rgba(0, 0, 0, .8)',
  semiTransparentblack90: 'rgba(0, 0, 0, .9)',
  semiTransparentwave90: 'rgba(61, 103, 120, .9)'
};
var colorAliases = {
  background: colors.lightSand,
  overlay: transparentColors.semiTransparentblack,
  shadow: colors.mediumGrayL1,
  textGray: colors.mediumGray,
  textwhite: colors.white,
  text: colors.darkGray,
  validationError: colors.morello,
  validationValid: colors.ocean
};

var Overlay = styled.div.withConfig({
  displayName: "overlay__Overlay",
  componentId: "sc-1n4pz2a-0"
})(["background:", ";height:100%;left:0;-webkit-overflow-scrolling:touch;overflow-y:auto;position:fixed;top:0;width:100%;z-index:10000;"], transparentColors.semiTransparentblack);

var Overlay$1 = function Overlay$1(_ref) {
  var onClick = _ref.onClick,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["onClick", "children"]);

  if (typeof onClick !== 'function' || !children) {
    return false;
  }

  var click = function click(e) {
    e.stopPropagation();
    onClick();
  };

  return React.createElement(Overlay, _extends({
    onClick: function onClick(e) {
      return click(e);
    }
  }, rest), children);
};

Overlay$1.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string
};
Overlay$1.defaultProps = {
  onClick: null,
  children: null,
  className: null
};

var fonts = {
  bold: "'ThomasHeadlineBold', 'Arial Black'",
  "default": "'Source Sans Pro', 'Arial'",
  regular: "'ThomasHeadlineRegular', 'Arial'",
  wave: "'NLTGWaveSerif', 'Arial'"
};

var deviceWidths = {
  smallMobile: '320px',
  mobile: '375px',
  largeMobile: '414px',
  xLargeMobile: '435px',
  mobileLandscape: '480px',
  mobileLandscapeMax: '479px',
  largeMobileLandscape: '568px',
  largeMobileLandscapeMax: '567px',
  tablet: '768px',
  tabletMax: '767px',
  desktop: '975px',
  desktopMax: '974px',
  tabletLandscape: '1024px',
  tabletLandscapeMax: '1023px',
  mediumDesktop: '1280px',
  mediumDesktopMax: '1278px',
  largeDesktop: '1680px',
  largeDesktopMax: '1679px',
  xLargeDesktop: '1920px'
};

var Title = styled.div.withConfig({
  displayName: "header__Title",
  componentId: "sc-1mmqwbq-0"
})(["flex-grow:1;font-family:", ";font-size:24px;h1,h2,h3,h4{font-family:", ";font-size:24px;}"], fonts.bold, fonts.bold);
var Header = styled.div.withConfig({
  displayName: "header__Header",
  componentId: "sc-1mmqwbq-1"
})(["align-items:center;background:", ";border-bottom:", " solid 1px;display:flex;justify-content:space-between;min-height:72px;padding:16px 0 16px 16px;position:sticky;top:0;z-index:1;", " @media (min-width:", "){border-bottom:0;padding:24px 0 0 32px;position:static;}.sg-popup-cancel-icon{align-self:flex-start;box-sizing:border-box;cursor:pointer;fill:", ";margin:10px 30px 0 30px;min-width:18px;width:18px;height:18px;@media (min-width:", "){margin:15px 30px 0 30px;}@media (min-width:", "){", "}}"], colors.white, colors.lightGray, function (props) {
  return props.roundedTop && css(["border-top-left-radius:30px;border-top-right-radius:30px;"]);
}, deviceWidths.tablet, colors.darkGray, deviceWidths.tablet, deviceWidths.desktop, function (props) {
  return props.tablet && css(["display:none;"]);
});

var Header$1 = function Header$1(_ref) {
  var close = _ref.close,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["close", "children"]);

  if (close && typeof close !== 'function' || !close && !children) {
    return false;
  }

  var click = function click(e) {
    e.stopPropagation();
    close();
  };

  return React.createElement(Header, rest, React.createElement(Title, null, children), close && React.createElement(Icon$1, {
    className: "sg-popup-cancel-icon",
    path: USER_INTERFACE_CROSS_CANCEL,
    onClick: function onClick(e) {
      return click(e);
    },
    role: "button"
  }));
};

Header$1.propTypes = {
  close: PropTypes.func,
  children: PropTypes.node
};
Header$1.defaultProps = {
  close: null,
  children: null
};

var Body = styled.div.withConfig({
  displayName: "body__Body",
  componentId: "sc-wnstwk-0"
})(["box-sizing:border-box;overflow-y:auto;@media (max-width:", "){flex-grow:1;height:0;max-width:100vw;}", ""], deviceWidths.tabletMax, function (props) {
  return props.defaultPadding && css(["width:100%;padding:32px 16px;@media (min-width:", "){padding:40px 32px;}"], deviceWidths.tablet);
});

var Body$1 = function Body$1(_ref) {
  var children = _ref.children,
      defaultPadding = _ref.defaultPadding,
      rest = _objectWithoutProperties(_ref, ["children", "defaultPadding"]);

  if (!children) {
    return false;
  }

  return React.createElement(Body, _extends({
    defaultPadding: defaultPadding
  }, rest), children);
};

Body$1.propTypes = {
  children: PropTypes.node,
  defaultPadding: PropTypes.bool
};
Body$1.defaultProps = {
  children: null,
  defaultPadding: false
};

var transitionSpeeds = {
  "default": '.25s',
  smooth: '.5s',
  slow: '1s'
};
var transitions = {
  "default": "".concat(transitionSpeeds["default"], " ease"),
  smooth: "".concat(transitionSpeeds.smooth, " ease"),
  slow: "".concat(transitionSpeeds.slow, " ease")
};

var PopUp = styled.div.withConfig({
  displayName: "popUp__PopUp",
  componentId: "sc-puogcl-0"
})(["background-color:", ";display:flex;flex-direction:column;margin:0;max-height:100vh;min-height:100vh;position:relative;transition:right ", ",left ", ";width:100vw;", " @media (min-width:", "){margin:80px auto;max-height:none;max-width:", ";min-height:100px;}.sg-popup-cancel-icon-bottom{align-self:flex-start;box-sizing:border-box;cursor:pointer;fill:", ";margin:15px 0 30px calc(100% - 30px - 18px);width:18px;height:18px;}", " ", " ", " ", " ", ""], colors.white, transitions["default"], transitions["default"], function (props) {
  return props.roundedTop && css(["border-top-left-radius:30px;border-top-right-radius:30px;"]);
}, deviceWidths.tablet, deviceWidths.xLargeMobile, colors.darkGray, function (props) {
  return props.large && css(["@media (min-width:", "){max-width:900px;}"], deviceWidths.tabletMax);
}, function (props) {
  return props.mobile && css(["left:0;-webkit-overflow-scrolling:touch;overflow-y:auto;position:fixed;top:0;z-index:10000;@media (min-width:", "){box-shadow:0 0 1px ", ";height:auto;margin:2% 0 0;max-width:975px;position:relative;width:auto;}"], deviceWidths.tabletMax, colorAliases.shadow);
}, function (props) {
  return props.mobile && props.closed && css(["display:none;"]);
}, function (props) {
  return props.tablet && css(["left:0;-webkit-overflow-scrolling:touch;overflow-y:auto;position:fixed;top:0;z-index:10000;@media (min-width:", "){box-shadow:0 0 1px ", ";height:auto;margin:0;max-width:975px;position:relative;width:auto;}"], deviceWidths.desktopMax, colorAliases.shadow);
}, function (props) {
  return props.tablet && props.closed && css(["display:none;"]);
});

var PopUp$1 = function PopUp$1(props) {
  var closed = props.closed,
      _close = props.close,
      closeBottom = props.closeBottom,
      header = props.header,
      children = props.children,
      defaultPadding = props.defaultPadding,
      roundedTop = props.roundedTop,
      rest = _objectWithoutProperties(props, ["closed", "close", "closeBottom", "header", "children", "defaultPadding", "roundedTop"]);

  if (closed || _close && typeof _close !== 'function' || !children) {
    return false;
  }

  var _useState = useState({
    token: ''
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var onEvent = function onEvent(event) {
    if (keycode(event) === 'esc' && !closed) {
      _close();
    }
  };

  useEffect(function () {
    setState({
      token: bodyGuard.register()
    });
    document.addEventListener('keydown', onEvent, false);

    if (!closed) {
      bodyGuard.on(state.token);
    }

    return function () {
      bodyGuard.deregister(state.token);
      document.removeEventListener('keydown', onEvent, false);
    };
  }, [_close, closed]);
  useEffect(function () {
    if (typeof window === 'undefined') {
      return;
    }

    bodyGuard[closed ? 'off' : 'on'](state.token);
  }, [_close]);

  var popupClick = function popupClick(e) {
    e.stopPropagation();
  };

  return React.createElement(Overlay$1, {
    onClick: function onClick() {
      return _close();
    }
  }, React.createElement(PopUp, _extends({
    onClick: popupClick,
    roundedTop: roundedTop
  }, rest), React.createElement(Header$1, {
    close: function close() {
      return _close();
    },
    roundedTop: roundedTop
  }, header), React.createElement(Body$1, {
    defaultPadding: defaultPadding
  }, children), closeBottom && React.createElement(Icon$1, {
    className: "sg-popup-cancel-icon-bottom",
    path: USER_INTERFACE_CROSS_CANCEL,
    onClick: function onClick() {
      return _close();
    },
    role: "button"
  })));
};

PopUp$1.propTypes = {
  closed: PropTypes.bool,
  close: PropTypes.func,
  closeBottom: PropTypes.bool,
  className: PropTypes.string,
  header: PropTypes.node,
  children: PropTypes.node,
  defaultPadding: PropTypes.bool,
  roundedTop: PropTypes.bool
};
PopUp$1.defaultProps = {
  closed: false,
  close: null,
  closeBottom: false,
  className: null,
  header: null,
  children: null,
  defaultPadding: false,
  roundedTop: false
};

var PopUpMobile = function PopUpMobile(props) {
  var closed = props.closed,
      close = props.close,
      closeBottom = props.closeBottom,
      header = props.header,
      children = props.children,
      rest = _objectWithoutProperties(props, ["closed", "close", "closeBottom", "header", "children"]);

  if (typeof close !== 'function' || !children) {
    return false;
  }

  var _useState = useState({
    token: ''
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var onEvent = function onEvent(event) {
    if (keycode(event) === 'esc' && !closed) {
      close();
    }
  };

  var checkBodyguard = function checkBodyguard() {
    if (window.innerWidth < 768 && !closed) {
      bodyGuard.on(state.token);
    } else {
      bodyGuard.off(state.token);
    }
  };

  var onResize = function onResize() {
    checkBodyguard();
  };

  useEffect(function () {
    setState({
      token: bodyGuard.register()
    });
    document.addEventListener('keydown', onEvent, false);

    if (!closed) {
      checkBodyguard();
      window.addEventListener('resize', onResize, false);
    }

    return function () {
      bodyGuard.deregister(state.token);

      if (!closed) {
        document.removeEventListener('keydown', onEvent, false);
      }

      window.removeEventListener('resize', onResize, false);
    };
  }, []);
  useEffect(function () {
    if (typeof window === 'undefined') {
      return;
    }

    if (closed) {
      document.removeEventListener('keydown', onEvent, false);
    } else {
      document.addEventListener('keydown', onEvent, false);
    }

    checkBodyguard();
  }, [closed]);

  var popupClick = function popupClick(e) {
    e.stopPropagation();
  };

  var closeClick = function closeClick(e) {
    e.stopPropagation();
    close();
  };

  return React.createElement(PopUp, _extends({
    mobile: true,
    closed: closed,
    onClick: popupClick
  }, rest), React.createElement(Header$1, {
    close: close
  }, header), React.createElement(Body$1, null, children), closeBottom && React.createElement(Icon$1, {
    className: "sg-popup-cancel-icon-bottom",
    path: USER_INTERFACE_CROSS_CANCEL,
    onClick: function onClick(e) {
      return closeClick(e);
    },
    role: "button"
  }));
};

PopUpMobile.propTypes = {
  closed: PropTypes.bool,
  close: PropTypes.func,
  className: PropTypes.string,
  header: PropTypes.node,
  children: PropTypes.node,
  closeBottom: PropTypes.bool
};
PopUpMobile.defaultProps = {
  closed: true,
  close: null,
  className: null,
  header: null,
  children: null,
  closeBottom: false
};

var Footer = styled.div.withConfig({
  displayName: "footer__Footer",
  componentId: "sc-ekt5b9-0"
})(["background-color:", ";bottom:0;height:auto;padding:24px;position:absolute;width:100%;"], colors.lightGray);

var Footer$1 = function Footer$1(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["children"]);

  if (!children) {
    return false;
  }

  return React.createElement(Footer, rest, children);
};

Footer$1.propTypes = {
  children: PropTypes.node
};
Footer$1.defaultProps = {
  children: null
};

var PopUpTablet = function PopUpTablet(props) {
  var closed = props.closed,
      close = props.close,
      closeBottom = props.closeBottom,
      header = props.header,
      children = props.children,
      footer = props.footer,
      rest = _objectWithoutProperties(props, ["closed", "close", "closeBottom", "header", "children", "footer"]);

  if (typeof close !== 'function' || !children) {
    return false;
  }

  var _useState = useState({
    token: ''
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var onEvent = function onEvent(event) {
    if (keycode(event) === 'esc') {
      if (!closed) {
        close();
      }
    }
  };

  var checkBodyguard = function checkBodyguard() {
    if (window.innerWidth < 975 && !closed) {
      bodyGuard.on(state.token);
    } else {
      bodyGuard.off(state.token);
    }
  };

  var onResize = function onResize() {
    checkBodyguard();
  };

  useEffect(function () {
    setState({
      token: bodyGuard.register()
    });

    if (!closed) {
      document.addEventListener('keydown', onEvent, false);
    }

    checkBodyguard();
    window.addEventListener('resize', onResize, false);
    return function () {
      bodyGuard.deregister(state.token);

      if (!closed) {
        document.removeEventListener('keydown', onEvent, false);
      }

      window.removeEventListener('resize', onResize, false);
    };
  }, []);
  useEffect(function (nextProps) {
    if (typeof window === 'undefined') {
      return;
    }

    if (nextProps) {
      document.removeEventListener('keydown', onEvent, false);
    } else {
      document.addEventListener('keydown', onEvent, false);
    }

    checkBodyguard();
  }, [close]);

  var popupClick = function popupClick(e) {
    e.stopPropagation();
  };

  return React.createElement(PopUp, _extends({
    onClick: popupClick,
    tablet: true,
    closed: closed
  }, rest), React.createElement(Header$1, {
    className: "sg-popup-header",
    close: close,
    tablet: true
  }, header), React.createElement(Body$1, null, children), closeBottom && React.createElement(Icon$1, {
    className: "sg-popup-cancel-icon2",
    path: USER_INTERFACE_CROSS_CANCEL,
    onClick: function onClick(e) {
      return popupClick(e);
    },
    role: "button"
  }), footer && React.createElement(Footer$1, null, footer));
};

PopUpTablet.propTypes = {
  closed: PropTypes.bool,
  close: PropTypes.func,
  className: PropTypes.string,
  header: PropTypes.node,
  children: PropTypes.node,
  closeBottom: PropTypes.bool,
  footer: PropTypes.node
};
PopUpTablet.defaultProps = {
  closed: false,
  close: null,
  className: null,
  header: null,
  children: null,
  closeBottom: false,
  footer: null
};

var PopUpLarge = function PopUpLarge(props) {
  return React.createElement(PopUp$1, _extends({
    large: true
  }, props));
};

var slideInRight = keyframes(["from{transform:translate3d(100%,0,0);}to{transform:translate3d(0,0,0);}"]);
var slideInLeft = keyframes(["from{transform:translate3d(-100%,0,0);}to{transform:translate3d(0,0,0);}"]);
var SlideIn = styled.div.withConfig({
  displayName: "slideIn__SlideIn",
  componentId: "sc-19a9uqm-0"
})(["animation-duration:0.25s;animation-name:", ";background-color:", ";bottom:0;display:flex;flex-direction:column;max-width:", ";overflow-y:auto;position:absolute;left:0;top:0;width:100%;", ""], slideInLeft, colors.white, deviceWidths.largeMobile, function (props) {
  return props.right && css(["animation-name:", ";left:auto;right:0;"], slideInRight);
});

var SlideIn$1 = function SlideIn$1(props) {
  var closed = props.closed,
      close = props.close,
      className = props.className,
      header = props.header,
      children = props.children,
      footer = props.footer,
      defaultPadding = props.defaultPadding,
      rest = _objectWithoutProperties(props, ["closed", "close", "className", "header", "children", "footer", "defaultPadding"]);

  if (closed || typeof close !== 'function' || !children) {
    return false;
  }

  var _useState = useState({
    token: ''
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var onEvent = function onEvent(event) {
    if (keycode(event) === 'esc') {
      if (!closed) {
        close();
      }
    }
  };

  useEffect(function () {
    setState({
      token: bodyGuard.register()
    });
    document.addEventListener('keydown', onEvent, false);

    if (!closed) {
      bodyGuard.on(state.token);
    }

    return function () {
      bodyGuard.deregister(state.token);
      document.removeEventListener('keydown', onEvent, false);
    };
  }, [close, closed]);
  useEffect(function () {
    if (typeof window === 'undefined') {
      return;
    }

    bodyGuard[closed ? 'off' : 'on'](state.token);
  }, [close]);

  var slideinClick = function slideinClick(e) {
    e.stopPropagation();
  };

  return React.createElement(Overlay$1, {
    onClick: close
  }, React.createElement(SlideIn, _extends({
    onClick: slideinClick
  }, rest), React.createElement(Header$1, {
    close: close
  }, header), React.createElement(Body$1, {
    defaultPadding: defaultPadding
  }, children), footer && React.createElement(Footer$1, null, footer)));
};

SlideIn$1.propTypes = {
  closed: PropTypes.bool,
  close: PropTypes.func,
  className: PropTypes.string,
  header: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  defaultPadding: PropTypes.bool
};
SlideIn$1.defaultProps = {
  closed: false,
  close: null,
  className: null,
  header: null,
  children: null,
  footer: null,
  defaultPadding: false
};

var SlideInLeft = function SlideInLeft(props) {
  return React.createElement(SlideIn$1, props);
};

var SlideInRight = function SlideInRight(props) {
  return React.createElement(SlideIn$1, _extends({
    right: true
  }, props));
};

export { PopUp$1 as PopUp, PopUpLarge, PopUpMobile, PopUpTablet, SlideIn$1 as SlideIn, SlideInLeft, SlideInRight };
//# sourceMappingURL=index.js.map
